import {Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {RegistrationService} from '@app/features/registration/services/registration.service';
import {LoadSpinnerWrapperComponent} from '@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component';
import {
  PrimaryActionButtonComponent
} from '@app/shared/components/primary-action-button/primary-action-button.component';
import {
  SecondaryActionButtonComponent
} from '@app/shared/components/secondary-action-button/secondary-action.component';
import {CommonDirectives} from '@app/shared/directives/common-directives';
import {AccountTypes} from '@app/shared/enums/accountTypes.enum';
import {UserStatus} from '@app/shared/enums/userStatus.enum';
import {CaseContact} from '@app/shared/interfaces/caseContact.interface';
import {Clinic} from '@app/shared/interfaces/clinic.interface';
import {Provider} from '@app/shared/interfaces/provider.interface';
import {AccountTypeNamePipe} from '@app/shared/pipes/accountTypeName.pipe';
import {DateFormatPipe} from '@app/shared/pipes/dateFormat.pipe';
import {PhoneFormatPipe} from '@app/shared/pipes/phoneFormat.pipe';
import {getAccountType} from '@app/shared/services/helpers';
import {debounceTime, Observable, of, Subscription, switchMap, take} from 'rxjs';
import {AdminService} from '../../services/admin.service';
import {ToastService} from '@app/core/services/toast.service';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {ConfirmDialogComponent} from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import {UserAvailability} from '@app/features/registration/interfaces/userAvailability.interface';
import {WebResponse} from '@app/shared/interfaces/webResponse.interface';
import {AdminInfoUpdateRequest} from '../../interfaces/adminInfoUpdateRequest.interface';
import {
  ClinicProviderInfoSectionComponent
} from '../shared/clinic-provider-info-section/clinic-provider-info-section.component';
import {Store} from '@ngrx/store';
import {AppState} from '@app/core/store/app.state';
import {CaseContactSearchTermChanged, FetchCaseContacts, FetchClinics, FetchProviders} from '../../store/admin.actions';
import {selectCaseContacts, selectClinics, selectProviders} from '../../store/admin.selectors';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {AuthResponse} from "@app/features/login/interfaces/authResponse.interface";

import {
  ReferralUserInfoUpdateRequest
} from "@app/features/adminDashboard/interfaces/referralUserInfoUpdateRequest.interface";
import {NgxMaskDirective, provideNgxMask} from 'ngx-mask';
import {STEPPER_GLOBAL_OPTIONS} from "@angular/cdk/stepper";
import {UserDetailsService} from "@app/shared/services/userDetails.service";
import {NgxMatSelectSearchModule} from "ngx-mat-select-search";
import {ReferralUser, User, WorkCompUser} from "@app/core/interfaces/user.interface";
import {
  ClinicProviderInfoPropsProvider,
  ClinicProviderInfoSectionProps
} from "@app/features/adminDashboard/interfaces/clinicProviderInfoSectionProps.interface";
import {us_states} from "@app/shared/interfaces/us_states";
import {environment} from "@env/environment";
import {ImpersonationRequested} from "@app/core/store/userAuth/userAuth.actions";

@Component({
  selector: 'app-user-mgmt-user-info-modal',
  imports: [
    MatDialogModule,
    LoadSpinnerWrapperComponent,
    CommonDirectives,
    MatIconModule,
    DateFormatPipe,
    AccountTypeNamePipe,
    PhoneFormatPipe,
    SecondaryActionButtonComponent,
    PrimaryActionButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    NgxMatSelectSearchModule,
    ClinicProviderInfoSectionComponent,
    MatOption,
    MatSelect,
    NgxMaskDirective
  ],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: {displayDefaultIndicatorType: false},
    },
    provideNgxMask()
  ],
  templateUrl: './user-mgmt-user-info-modal.component.html',
  styleUrl: './user-mgmt-user-info-modal.component.scss'
})
export class UserMgmtUserInfoModalComponent implements OnInit {
  @ViewChild('caseContactSelect') caseContactSelect!: MatSelect;
  @Input() userDetails!: User|ReferralUser|WorkCompUser;
  @Input() userAccountTypeId!: number;
  @Output() onSuspendUserSuccess = new EventEmitter<void>();
  @Output() onReactivateUserSuccess = new EventEmitter<void>();

  readonly impersonateAllowed: boolean = environment.impersonateAllowed;
  loading = false;
  readOnlyMode = true;
  subscriptions = new Subscription();
  userAccountType!: AccountTypes;
  AllAccountTypes = AccountTypes;
  cpInfoForm!: FormGroup;
  referralValues!: FormGroup;
  firstClinicCtrl!: FormControl;
  firstProvidersCtrl!: FormControl;
  secondClinicCtrl!: FormControl;
  secondProvidersCtrl!: FormControl;
  readonly USER_STATUS = UserStatus;
  readonly us_states_list = us_states;

  firstClinicProps: ClinicProviderInfoSectionProps | null = null;
  secondClinicProps: ClinicProviderInfoSectionProps | null = null;

  // TODO: Remove providersList and clinicsList after NextGen API integration
  // Clinics and Providers info will be included in UserDetails response after NextGen API integration
  clinicsList: Clinic[] = [];
  providersList: Provider[] = [];
  caseContactsList: CaseContact[] = [];
  caseContactSearchControl = new FormControl<string>('', {nonNullable: true});
  noCaseContactsFoundText = 'No contacts found';
  selectedCaseContactId: string | null = null;
  selectedCaseContact: CaseContact | null = null;
  initialCaseContactId: string | null = null;
  caseContactRequiredError = false;
  secondProviderError = false;

  personalInfoForm!: FormGroup;

  companyInfoFormGroup!: FormGroup;

  constructor(
    private userDetailsService: UserDetailsService,
    private dialogRef: MatDialogRef<UserMgmtUserInfoModalComponent>,
    private registrationService: RegistrationService,
    private adminService: AdminService,
    private toastService: ToastService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.subscribeToCloseEventSubscriptions();
    this.userAccountType = getAccountType(this.userAccountTypeId);
    this.subscriptions.add(
      this.caseContactSearchControl.valueChanges.pipe(debounceTime(1200)).subscribe(() => {
        if(this.caseContactSelect.panelOpen) {
          this.filterCaseContacts();
        }
      })
    );
    this.subscribeToCaseContacts();
    this.subscribeToClinicsAndProviders();
    if (this.isWorkCompAccountType()) {
      this.store.dispatch(FetchCaseContacts());
    }
    this.store.dispatch(FetchClinics());
    this.store.dispatch(FetchProviders());
    this.syncSelectedCaseContactId();
    this.syncUserDetailsWithClinicProviderNames();
  }

  getProviderName(providerId: string): ClinicProviderInfoPropsProvider {
    const provider = this.providersList.find(p => p.id == providerId);
    var name = provider ? `${provider.firstName} ${provider.lastName}` : '';
    if(provider?.degree) name += ", " + provider?.degree;
    return {
      firstName: provider?.firstName ?? '',
      lastName: provider?.lastName ?? '',
      degree: provider?.degree ?? '',
      id: providerId,
      name: name
    }
  }

  filterCaseContacts(): void {
    const search = this.caseContactSearchControl.value.trim().toLowerCase();
    this.store.dispatch(CaseContactSearchTermChanged({ searchTerm: search}));
  }

  private subscribeToClinicsAndProviders(): void {
    this.subscriptions.add(
      this.store.select(selectClinics).subscribe((clinics: Clinic[]) => {
        this.clinicsList = clinics;
        this.syncUserDetailsWithClinicProviderNames();
      })
    );

    this.subscriptions.add(
      this.store.select(selectProviders).subscribe((providers: Provider[]) => {
        this.providersList = providers;
        this.syncUserDetailsWithClinicProviderNames();
      })
    );
  }

  private subscribeToCaseContacts(): void {
    this.subscriptions.add(
      this.store.select(selectCaseContacts).subscribe((caseContacts: CaseContact[]) => {
        this.caseContactsList = caseContacts;
      })
    );
  }

  Impersonate() {
    this.store.dispatch(ImpersonationRequested({ impersonatedUserId: this.userDetails.subId }));
    this.dialogRef.close();
  }


  private getSelectedCaseContactId(): string | null {
    const caseContactId = 'caseContactId' in this.userDetails ? this.userDetails?.caseContactId : null;

    if (caseContactId === null || caseContactId === undefined || caseContactId === '') {
      return null;
    }

    return caseContactId;
  }

  private syncSelectedCaseContactId(): void {
    const selectedCaseContactId = this.getSelectedCaseContactId();

    if (!selectedCaseContactId) {
      this.subscribeToCaseContacts();
      this.selectedCaseContactId = null;
      this.selectedCaseContact = null;
      return;
    }

    this.selectedCaseContactId = selectedCaseContactId;

    this.subscriptions.add(
      this.registrationService.getCaseContact$(selectedCaseContactId)
        .subscribe((caseContact) => {
          if(caseContact) {
            this.caseContactsList = [caseContact];
          }
          this.selectedCaseContact = caseContact;
        })
    );
  }

  onCaseContactSelectionChange(caseContactId: string): void {
    this.selectedCaseContactId = caseContactId ?? null;
    if (!caseContactId) {
      this.selectedCaseContact = null;
      return;
    }

    this.selectedCaseContact = this.caseContactsList.find(caseContact =>
      caseContact.caseContactId === caseContactId) || null;

    if (this.hasValidCaseContactSelection()) {
      this.caseContactRequiredError = false;
    }
  }

  hasValidCaseContactSelection(): boolean {
    if (!this.selectedCaseContactId) {
      return false;
    }

    return !!this.selectedCaseContact;
  }

  private IsSecondProviderValid(): boolean {
    if(this.userAccountType !== AccountTypes.REFERRAL_PARTNER) {
      return true;
    }

    if(this.cpInfoForm.get('secondClinicId')?.value != null){
      return this.cpInfoForm.get('secondClinicProviderIds')?.value?.length > 0;
    }
    return (this.cpInfoForm.get('secondClinicProviderIds')?.value?.length ?? 0) == 0;
  }

  private hasCaseContactSelectionChanged(): boolean {
    if(!this.isWorkCompAccountType())
    {
      return false;
    }

    return this.toWorkCompUser(this.userDetails).caseContactId !== this.selectedCaseContactId;
  }

  private syncUserDetailsWithClinicProviderNames(): void {
    const userDetails = ('firstClinicId' in this.userDetails ? this.userDetails : null) as ReferralUser;

    if(!userDetails) return;

    const firstClinicProps = { ...this.firstClinicProps };
    const secondClinicProps = { ...this.secondClinicProps };

    if (userDetails.firstClinicId) {
      firstClinicProps.clinicId = userDetails.firstClinicId;
      firstClinicProps.clinicAddresses = this.clinicsList.find(clinic => clinic.name === userDetails.firstClinicId)?.addresses ?? [];
    }
    const secondClinicId = userDetails.pendingRemovalSecondClinicId ?? userDetails.secondClinicId;

    if (secondClinicId) {
      secondClinicProps.clinicId = secondClinicId;
      secondClinicProps.clinicAddresses = this.clinicsList.find(clinic => clinic.name === secondClinicId)?.addresses ?? [];
    }

    secondClinicProps.pendingRemovalClinicId = userDetails.pendingRemovalSecondClinicId ?? null;
    if (userDetails.firstClinicProviderIds?.length ?? 0 > 0) {
      firstClinicProps.providers =
        userDetails.firstClinicProviderIds?.map((id: string) => { return this.getProviderName(id)});
    }
    if (userDetails.pendingFirstClinicProviderIds?.length ?? 0 > 0) {
      firstClinicProps.pendingProviders =
        userDetails.pendingFirstClinicProviderIds?.map((id: string) => { return this.getProviderName(id)});
    }
    if (userDetails.pendingRemovalFirstClinicProviderIds?.length ?? 0 > 0) {
      firstClinicProps.pendingRemovalProviders =
        userDetails.pendingRemovalFirstClinicProviderIds?.map((id: string) => { return this.getProviderName(id)});
    }
    if (userDetails.requestedRemovalFirstClinicProviderIds?.length ?? 0 > 0) {
      firstClinicProps.requestedRemovalProviders =
        userDetails.requestedRemovalFirstClinicProviderIds?.map((id: string) =>{ return this.getProviderName(id)});
    } else {
      firstClinicProps.requestedRemovalProviders = [];
    }
    if (userDetails.secondClinicProviderIds?.length ?? 0 > 0) {
      secondClinicProps.providers =
        userDetails.secondClinicProviderIds?.map((id: string) => { return this.getProviderName(id)});
    }
    if (userDetails.pendingSecondClinicProviderIds?.length ?? 0 > 0) {
      secondClinicProps.pendingProviders =
        userDetails.pendingSecondClinicProviderIds?.map((id: string) => { return this.getProviderName(id)});
    }
    if (userDetails.pendingRemovalSecondClinicProviderIds?.length ?? 0 > 0) {
      secondClinicProps.pendingRemovalProviders =
        userDetails.pendingRemovalSecondClinicProviderIds?.map((id: string) => { return this.getProviderName(id)});
    }
    if (userDetails.requestedRemovalSecondClinicProviderIds?.length ?? 0 > 0) {
      secondClinicProps.requestedRemovalProviders =
        userDetails.requestedRemovalSecondClinicProviderIds?.map((id: string) => { return this.getProviderName(id)});
    } else {
      secondClinicProps.requestedRemovalProviders = [];
    }

    firstClinicProps.otherProviderName = userDetails.firstClinicOtherProviderName;
    firstClinicProps.otherProviderNpi = userDetails.firstClinicOtherProviderNPI;

    secondClinicProps.otherProviderName = userDetails.secondClinicOtherProviderName;
    secondClinicProps.otherProviderNpi = userDetails.secondClinicOtherProviderNPI;

    this.firstClinicProps = firstClinicProps as ClinicProviderInfoSectionProps;
    this.secondClinicProps = secondClinicProps as ClinicProviderInfoSectionProps;
  }

  private parseClinicId(clinicId: unknown): string | null {
    if (clinicId === null || clinicId === undefined || clinicId === '') {
      return null;
    }

    const parsedId = String(clinicId).trim();
    return parsedId.length > 0 ? parsedId : null;
  }

  handleClose() {
    if (this.anyUnsavedChanges) {
      this.showUnsavedChangesConfirmDialog(() => {
        this.subscriptions.unsubscribe();
        this.dialogRef.close();
      });
    } else {
      this.subscriptions.unsubscribe();
      this.dialogRef.close();
    }
  }

  onEditUser() {
    this.switchToEditMode();
    this.setupFormGroups();
    this.patchFormValues();
  }

  onSuspendUser() {
    this.loading = true;
    this.adminService.suspendUser$(this.userDetails.subId)
      .subscribe(response => {
        this.loading = false;
        if (response.succeeded) {
          this.onSuspendUserSuccess.emit();
          this.toastService.showSuccessToast('User suspended successfully.');
          this.dialogRef.close();
        } else {
          this.toastService.showErrorToast('Unable to suspend user. Please try again.');
        }
      });
  }

  onReactivateUser() {
    this.loading = true;
    this.adminService.reActivateUser$(this.userDetails.subId)
      .subscribe(response => {
        this.loading = false;
        if (response.succeeded) {
          this.onReactivateUserSuccess.emit();
          this.toastService.showSuccessToast('User reactivated successfully.');
          this.dialogRef.close();
        } else {
          this.toastService.showErrorToast('Unable to reactivate user. Please try again.');
        }
      });
  }

  onDeleteUser(): void {
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Delete User';
    dialog.componentInstance.content = `
    Are you sure you want to delete this user?
    This will also reset their MFA.
    The user will need to re-register to regain access
    `;
    dialog.componentInstance.confirmButtonTitle = 'Delete User';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => this.deleteUser());
  }

  private deleteUser(): void {
    this.loading = true;
    this.adminService.deleteUser$(this.userDetails.subId)
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.loading = false;
          if (response.succeeded) {
            this.toastService.showSuccessToast('User deleted successfully.');
            this.dialogRef.close();
          } else {
            this.toastService.showErrorToast('Unable to delete user. Please try again.');
          }
        },
        error: () => {
          this.loading = false;
          this.toastService.showErrorToast('Unable to delete user. Please try again.');
        }
      });
  }

  onCancel() {
    if (this.anyUnsavedChanges) {
      this.showUnsavedChangesConfirmDialog(() => this.switchToReadOnlyMode());
    } else {
      this.switchToReadOnlyMode();
    }
  }

  onSave() {
    if (this.isCaseContactRequiredForSave() && !this.hasValidCaseContactSelection()) {
      this.caseContactRequiredError = true;
      return;
    }
    this.caseContactRequiredError = false;
    if (!this.IsSecondProviderValid()) {
      this.secondProviderError = true;
      return;
    }
    this.secondProviderError = false;
    if (!this.shouldSaveInitiate()) { console.log('Save not initiated'); return; }
    this.saveUpdatedInfo();
  }

  onFirstClinicOtherProviderRemoved() {
    if(!this.cpInfoForm) return;

    this.cpInfoForm.patchValue(
      {
        firstClinicOtherProviderName: null,
        firstClinicOtherProviderNPI: null
      }
    );
    this.cpInfoForm.markAsDirty();
  }

  onSecondClinicOtherProviderRemoved() {
    this.cpInfoForm.patchValue(
      {
        secondClinicOtherProviderName: null,
        secondClinicOtherProviderNPI: null
      }
    );
    this.cpInfoForm.markAsDirty();
  }

  onFirstClinicRemovalRequestRemoved() {
    if(!this.cpInfoForm) return;

    this.cpInfoForm.patchValue(
      {
        firstClinicRemovalRequests: [],
      }
    );
    this.cpInfoForm.markAsDirty();
  }

  onSecondClinicRemovalRequestRemoved() {
    this.cpInfoForm.patchValue(
      {
        secondClinicRemovalRequests: [],
      }
    );
    this.cpInfoForm.markAsDirty();
  }

  private isWorkCompAccountType(): boolean {
    return this.userAccountType === AccountTypes.EMPLOYER_OF_WORK_COMP_PATIENT
      || this.userAccountType === AccountTypes.CASE_MANAGER
      || this.userAccountType === AccountTypes.INSURANCE_ADJUSTER;
  }

  isCaseContactRequiredForSave(): boolean {
    return this.isWorkCompAccountType();
  }

  private shouldSaveInitiate(): boolean {
    return this.anyUnsavedChanges &&
      (this.personalInfoForm?.valid ?? true) &&
      (this.referralValues?.valid ?? true) &&
      (this.companyInfoFormGroup?.valid ?? true) &&
      (this.cpInfoForm?.valid ?? true);

  }

  private saveUpdatedInfo() {
    this.loading = true;
    const email: string = this.personalInfoForm.value.email ?? '';
    if (email !== this.userDetails.email) {
    this.registrationService.checkEmailAvailability(email)
      .pipe(switchMap((response: UserAvailability) => {
        if (response.emailTaken) {
          this.loading = false;
          this.setPersonalInfoErrors();
          return of(false);
        } else {
          this.removePersonalInfoErrors();
          return of(true);
        }
      }))
      .subscribe((emailAvailable: boolean) => {
        if (emailAvailable) {
          this.saveInfo(email);
        }
      });
    } else {
      this.saveInfo(email);
    }
  }

  private saveInfo(email: string){
    let userUpdate$: Observable<WebResponse<boolean>> | null = null;

    switch (this.userAccountType) {
      case AccountTypes.ADMIN:
        const adminUpdatedInfo: AdminInfoUpdateRequest = { email };
        userUpdate$ = this.adminService.updateAdminUserInfoForUserMgmt(this.userDetails.subId, adminUpdatedInfo);
        break;
      case AccountTypes.EMPLOYER_OF_WORK_COMP_PATIENT:
      case AccountTypes.CASE_MANAGER:
      case AccountTypes.INSURANCE_ADJUSTER:
        const workCompUpdatedInfo = this.getUserValuesFromForm(email) as WorkCompUser;
        console.log('Updating work comp user info:', workCompUpdatedInfo);
        userUpdate$ = this.adminService.updateWorkCompUserInfoForUserMgmt(this.userDetails.subId, workCompUpdatedInfo);
        break;
      case AccountTypes.REFERRAL_PARTNER:
        const model = this.getUserValuesFromForm(email) as ReferralUser;
        userUpdate$ = this.adminService.updateReferralUserInfoForUserMgmt(this.userDetails.subId, model);
        break;
    }

    if(userUpdate$ === null) return;

    userUpdate$.pipe(
      take(1),
      switchMap((response: WebResponse<boolean>)=> {
        this.onUpdateResponseFromServer(response);
        return response.succeeded
          ? this.userDetailsService.getUserDetails$(this.userDetails.subId, this.userAccountType).pipe(take(1))
          : of(null);
      })
    ).subscribe(newUserDetails => {
      if(newUserDetails) {
        this.userDetails = newUserDetails;
        // Unset these, they're set in the sync function and keeping them set just leads to possible stale values
        this.firstClinicProps = null;
        this.secondClinicProps = null;
        this.syncSelectedCaseContactId();
        this.syncUserDetailsWithClinicProviderNames();
        this.patchFormValues();
      }
    });
  }

  private onUpdateResponseFromServer(response: WebResponse<boolean>) {
    this.loading = false;
    if (response.succeeded) {
      this.toastService.showSuccessToast('User information updated successfully.');
      this.switchToReadOnlyMode();
    } else {
      this.toastService.showErrorToast('Unable to update user information. Please try again.');
    }
  }

  private updateUserDetailsAfterSuccessfulUpdate(email: string) {
    const updatedUserDetails = { ...this.userDetails, email };
    if (this.isWorkCompAccountType()) {
      // updatedUserDetails.caseContactId = this.selectedCaseContactId;
      // if (this.selectedCaseContact) {
      //   updatedUserDetails.caseContact = this.selectedCaseContact;
      //   updatedUserDetails.nextGenCaseContact = this.selectedCaseContact;
      // }
    }
    this.userDetails = updatedUserDetails;
    this.initialCaseContactId = this.selectedCaseContactId;
  }

  private setPersonalInfoErrors() {
    this.personalInfoForm.get('email')?.setErrors({ emailTaken: true });
  }

  private removePersonalInfoErrors() {
    this.personalInfoForm.get('email')?.setErrors(null);
  }

  private showUnsavedChangesConfirmDialog(onConfirm: () => void) {
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Unsaved Changes';
    dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to leave this page?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => onConfirm());
  }

  private setupFormGroups() {
    let personalInfoFormGroupProps = {};
    let companyInfoFormGroupProps = {};

    if (this.userAccountType === AccountTypes.REFERRAL_PARTNER) {
      this.cpInfoForm = this.fb.group({
        firstClinicId: [null, Validators.required],
        firstClinicProviderIds: [null, Validators.required],
        secondClinicId: [null],
        secondClinicProviderIds: [null],
        firstClinicOtherProviderName: [null as unknown as string],
        firstClinicOtherProviderNPI: [null as unknown as string],
        secondClinicOtherProviderName: [null as unknown as string],
        secondClinicOtherProviderNPI: [null as unknown as string],
      });

      this.referralValues = this.fb.group({
        jobTitle: [null, Validators.required],
      });

      this.firstClinicCtrl = this.cpInfoForm.controls['firstClinicId'] as FormControl;
      this.firstProvidersCtrl = this.cpInfoForm.controls['firstClinicProviderIds'] as FormControl;
      this.secondClinicCtrl = this.cpInfoForm.controls['secondClinicId'] as FormControl;
      this.secondProvidersCtrl = this.cpInfoForm.controls['secondClinicProviderIds'] as FormControl;

      const referralUser = this.userDetails as ReferralUser;
      if (referralUser.secondClinicId || referralUser.pendingRemovalSecondClinicId) {
        // If a second clinic is already selected, require second clinic providers
        this.secondProvidersCtrl.addValidators([Validators.required]);
      } else {
        // Otherwise, track changes to the second clinic and require providers if one is selected.
        this.subscriptions.add(
          this.secondClinicCtrl.valueChanges.subscribe((val) => {
            if (!val) return;

            if (!this.secondProvidersCtrl.hasValidator(Validators.required)) {
              this.secondProvidersCtrl.addValidators([Validators.required]);
            }
          })
        );
      }
    }

    personalInfoFormGroupProps = {
      email: [null as unknown as string, [Validators.required, Validators.email]],
      phone: [null as unknown as string, [Validators.required]],
      faxNumber: [null as unknown as string]
    };

    if(this.isWorkCompAccountType())
    {
      const wcUser = this.toWorkCompUser(this.userDetails);
      personalInfoFormGroupProps = {
        ...personalInfoFormGroupProps,
        address: [null as unknown as string, [Validators.required]],
        city: [null as unknown as string, [Validators.required]],
        state: [null as unknown as string, [Validators.required]],
        zipcode: [null as unknown as string, [Validators.required,Validators.pattern(/^\d{5}$/)]]
      }
    }

    if('managerFirstName' in this.userDetails) {
      companyInfoFormGroupProps = { ...companyInfoFormGroupProps,
        managerFirstName: [null as unknown as string, Validators.required],
        managerLastName: [null as unknown as string, Validators.required],
        managerEmail: [null as unknown as string, [Validators.required, Validators.email]],
        faxForInformation: [null as unknown as string, Validators.required],
        faxForChartNote: [null as unknown as string, Validators.required]
      };
    }

    if('companyName' in this.userDetails) {
      companyInfoFormGroupProps = {
        ...companyInfoFormGroupProps,
        companyName: [null as unknown as string, Validators.required]
      };
    }

    this.personalInfoForm = this.fb.group(personalInfoFormGroupProps);
    this.companyInfoFormGroup = this.fb.group(companyInfoFormGroupProps);
  }

  private patchFormValues() {
    let personalInfoFormPatchValues = {};

    personalInfoFormPatchValues = {
      email: this.userDetails.email,
      phone: this.userDetails.phoneNumber,
      faxNumber: this.userDetails.faxNumber
    };

    if(this.isWorkCompAccountType()) {
      personalInfoFormPatchValues = {
        ...personalInfoFormPatchValues,
        address: this.userDetails.address,
        city: this.userDetails.city,
        state: this.userDetails.state,
        zipcode: this.userDetails.postalCode
      }
    }

    this.personalInfoForm.patchValue(personalInfoFormPatchValues);
    this.personalInfoForm.markAsPristine();
    this.personalInfoForm.markAsUntouched();

    if (this.userAccountType === AccountTypes.ADMIN) {
      return;
    }

    this.initialCaseContactId = this.selectedCaseContactId;

    let companyInfoFormPatchValues = {};
    let referralFormPatchValues = {};
    let cpInfoFormPatchValues = {};

    if('managerFirstName' in this.userDetails) {
      companyInfoFormPatchValues = {
        ...companyInfoFormPatchValues,
        managerFirstName: this.userDetails?.managerFirstName,
        managerLastName: this.userDetails?.managerLastName,
        managerEmail: this.userDetails?.managerEmail,
        faxForInformation: this.userDetails?.faxForInformation,
        faxForChartNote: this.userDetails?.faxForChartNote
      };
    }

    if('companyName' in this.userDetails) {
      companyInfoFormPatchValues = {
        ...companyInfoFormPatchValues,
        companyName: this.userDetails?.companyName
      };
    }

    if (this.userAccountType === AccountTypes.REFERRAL_PARTNER) {
      const referralUser = this.userDetails as ReferralUser;

      referralFormPatchValues = {
        jobTitle: referralUser?.jobTitle
      };
      this.referralValues.patchValue(referralFormPatchValues);
      this.referralValues.markAsPristine();
      this.referralValues.markAsUntouched();

      const firstClinicId = this.parseClinicId(referralUser?.firstClinicId);
      const secondClinicId = this.parseClinicId(
        referralUser.pendingRemovalSecondClinicId ?? referralUser.secondClinicId
      );
      const secondClinicProviderIds = referralUser.pendingRemovalSecondClinicId
        ? Array.from(new Set([
          ...(referralUser.secondClinicProviderIds ?? []),
          ...(referralUser.pendingRemovalSecondClinicProviderIds ?? [])
        ]))
        : referralUser.secondClinicProviderIds ?? [];

      cpInfoFormPatchValues = {
        ...cpInfoFormPatchValues,
        firstClinicId,
        firstClinicProviderIds: (referralUser.firstClinicProviderIds ?? []),
        secondClinicId: secondClinicId,
        secondClinicProviderIds: secondClinicProviderIds,
        firstClinicOtherProviderName: referralUser?.firstClinicOtherProviderName,
        firstClinicOtherProviderNPI: referralUser?.firstClinicOtherProviderNPI,
        secondClinicOtherProviderName: referralUser.secondClinicOtherProviderName,
        secondClinicOtherProviderNPI: referralUser.secondClinicOtherProviderNPI
      };
    }

    if(this.companyInfoFormGroup && companyInfoFormPatchValues) {
      this.companyInfoFormGroup.patchValue(companyInfoFormPatchValues);
      this.companyInfoFormGroup.markAsPristine();
      this.companyInfoFormGroup.markAsUntouched();
    }

    if(this.cpInfoForm && cpInfoFormPatchValues) {
      this.cpInfoForm.patchValue(cpInfoFormPatchValues);
      this.companyInfoFormGroup.markAsPristine();
      this.companyInfoFormGroup.markAsUntouched();
    }
  }

  private subscribeToCloseEventSubscriptions() {
    this.subscriptions.add(
      this.dialogRef.beforeClosed().subscribe(() => this.handleClose())
    );

    this.subscriptions.add(
      this.dialogRef.backdropClick().subscribe(() => this.handleClose())
    );

    this.subscriptions.add(
      this.dialogRef.keydownEvents().subscribe(event => {
        if (event.key === 'Escape') { this.handleClose(); }
      })
    );
  }

  private switchToReadOnlyMode() {
    this.readOnlyMode = true;
    this.caseContactRequiredError = false;
    this.secondProviderError = false;
    this.syncSelectedCaseContactId();
  }

  private switchToEditMode() {
    this.readOnlyMode = false;
  }

  private isReadOnlyMode(): boolean {
    return this.readOnlyMode;
  }

  private getUserValuesFromForm(email: string): ReferralUser | WorkCompUser | User {
    const user: User = {
      id: this.userDetails.id,
      subId: this.userDetails.subId,
      userStatusLookupId: this.userDetails.userStatusLookupId,
      address: this.userDetails.address,
      faxNumber: this.userDetails.faxNumber,
      state: this.userDetails.state,
      firstName: this.userDetails.firstName,
      lastName: this.userDetails.lastName,
      postalCode: this.userDetails.postalCode,
      city: this.userDetails.city,
      phoneNumber: this.personalInfoForm.get('phone')?.value ?? this.userDetails.phoneNumber,
      email: email
    };

    switch(this.userAccountType) {
      case AccountTypes.CASE_MANAGER:
      case AccountTypes.INSURANCE_ADJUSTER:
      case AccountTypes.EMPLOYER_OF_WORK_COMP_PATIENT:
        return {
          ...user,
          address: this.personalInfoForm.get('address')?.value ?? this.userDetails.address,
          faxNumber: this.personalInfoForm.get('faxNumber')?.value ?? this.userDetails.faxNumber,
          state: this.personalInfoForm.get('state')?.value ?? this.userDetails.state,
          firstName: this.personalInfoForm.get('firstName')?.value ?? this.userDetails.firstName,
          lastName: this.personalInfoForm.get('lastName')?.value ?? this.userDetails.lastName,
          postalCode: this.personalInfoForm.get('zipcode')?.value ?? this.userDetails.postalCode,
          city: this.personalInfoForm.get('city')?.value ?? this.userDetails.city,
          caseContactId: this.selectedCaseContactId,
          companyName: this.companyInfoFormGroup?.get('companyName')?.value ?? '',
          managerEmail: this.companyInfoFormGroup?.get('managerEmail')?.value ?? '',
          managerFirstName: this.companyInfoFormGroup?.get('managerFirstName')?.value ?? '',
          managerLastName: this.companyInfoFormGroup?.get('managerLastName')?.value ?? '',
          faxForInformation: this.companyInfoFormGroup?.get('faxForInformation')?.value ?? '',
          faxForChartNote: this.companyInfoFormGroup?.get('faxForChartNote')?.value ?? '',
        } as WorkCompUser;
      case AccountTypes.REFERRAL_PARTNER:
        return {
          ...user,
          jobTitle: this.referralValues.get('jobTitle')?.value,
          firstClinicProviderIds: this.cpInfoForm.get('firstClinicProviderIds')?.value,
          secondClinicProviderIds: this.cpInfoForm.get('secondClinicProviderIds')?.value,
          firstClinicId: this.cpInfoForm.get('firstClinicId')?.value,
          secondClinicId: this.cpInfoForm.get('secondClinicId')?.value,
          firstClinicOtherProviderName: this.cpInfoForm.get('firstClinicOtherProviderName')?.value,
          firstClinicOtherProviderNPI: this.cpInfoForm.get('firstClinicOtherProviderNPI')?.value,
          secondClinicOtherProviderName: this.cpInfoForm.get('secondClinicOtherProviderName')?.value,
          secondClinicOtherProviderNPI: this.cpInfoForm.get('secondClinicOtherProviderNPI')?.value,
          // If there is no second clinic, then we use what is set for second clinic, which will be null
          // or set to what we want to remove.
          requestedRemovalSecondClinicId: (!!this.cpInfoForm.get('secondClinicId')?.value) ? null : this.toReferralUser(this.userDetails).secondClinicId,
          managerEmail: this.companyInfoFormGroup.get('managerEmail')?.value ?? '',
          managerFirstName: this.companyInfoFormGroup.get('managerFirstName')?.value ?? '',
          managerLastName: this.companyInfoFormGroup.get('managerLastName')?.value ?? '',
          faxForInformation: this.companyInfoFormGroup?.get('faxForInformation')?.value ?? '',
          faxForChartNote: this.companyInfoFormGroup?.get('faxForChartNote')?.value ?? '',
        } as ReferralUser
    }

    return user;
  }

  get anyUnsavedChanges(): boolean {
    if (this.isReadOnlyMode()) { return false; }

    return this.hasCaseContactSelectionChanged() ||
      this.personalInfoForm?.dirty ||
      this.referralValues?.dirty ||
      this.companyInfoFormGroup?.dirty ||
      this.cpInfoForm?.dirty;
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.anyUnsavedChanges) {
      event.preventDefault();
      event.returnValue = ''; // required for modern browsers
      return '';
    }
    return undefined;
  }

  toReferralUser(userDetails: User | ReferralUser | WorkCompUser): ReferralUser {
    return userDetails as ReferralUser;
  }

  toWorkCompUser(userDetails: User | ReferralUser | WorkCompUser): WorkCompUser {
    return userDetails as WorkCompUser;
  }
}
